import { useEffect, useRef, useState } from 'react';
import type { GameState } from '@flip7/shared';
import { calculateScore } from '@flip7/shared';
import { Card } from '../components/Card';
import { Avatar } from '../components/Avatar';
import { formatRoundEvent, eventIcon, eventColor } from '../utils/round-events';

interface TVModeScreenProps {
  gameState: GameState;
  onExitTV: () => void;
}

function PlayerCard({
  player,
  isCurrent,
  variant,
}: {
  player: GameState['players'][number];
  isCurrent: boolean;
  variant: GameState['variant'];
}) {
  const round = player.currentRound;
  const cards = round?.cards ?? [];
  const score = round
    ? calculateScore(cards, round.status === 'busted', variant)
    : null;

  return (
    <div
      className={`tv-player${isCurrent ? ' tv-player--active' : ''}${round?.status === 'busted' ? ' tv-player--busted' : ''}${round?.status === 'stayed' ? ' tv-player--stayed' : ''}${round?.status === 'frozen' ? ' tv-player--frozen' : ''}`}
    >
      <div className="tv-player__header">
        <div className="tv-player__name-row">
          <Avatar config={player.avatarConfig} emoji={player.avatar} size={36} />
          <span className="tv-player__name">
            {player.isBot && '\uD83E\uDD16 '}{player.name}
          </span>
          {player.title && <span className="tv-player__title">{player.title}</span>}
        </div>
        <div className="tv-player__scores">
          <span className="tv-player__total">{player.totalScore}</span>
          {round && round.status !== 'busted' && score && (
            <span className="tv-player__round-score">+{score.totalScore}</span>
          )}
        </div>
      </div>

      {/* Status badge */}
      {round && round.status !== 'active' && (
        <div className={`tv-player__status tv-player__status--${round.status}`}>
          {round.status === 'busted' && (
            <>BUST{round.bustCard?.type === 'number' ? ` (drew ${round.bustCard.value})` : ''}</>
          )}
          {round.status === 'stayed' && 'SETTLED'}
          {round.status === 'frozen' && '\u2744 FROZEN'}
        </div>
      )}

      {/* Cards in hand */}
      {cards.length > 0 && (
        <div className="tv-player__cards">
          {cards.map((card, i) => (
            <div key={`${card.type}-${card.value}-${i}`} style={{ pointerEvents: 'none', transform: 'scale(0.85)', transformOrigin: 'top left' }}>
              <Card card={card} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function TVModeScreen({ gameState, onExitTV }: TVModeScreenProps) {
  const [eventToast, setEventToast] = useState<string | null>(null);
  const prevEventsLenRef = useRef(gameState.roundEvents.length);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    const newLen = gameState.roundEvents.length;
    if (newLen > prevEventsLenRef.current && newLen > 0) {
      const latest = gameState.roundEvents[newLen - 1];
      setEventToast(`${eventIcon(latest.type)} ${formatRoundEvent(latest)}`);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setEventToast(null), 2500);
    }
    prevEventsLenRef.current = newLen;
  }, [gameState.roundEvents.length]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, []);

  const currentPlayer = gameState.players[gameState.currentPlayerIndex];
  const sortedPlayers = [...gameState.players].sort((a, b) => b.totalScore - a.totalScore);

  // For game over / round summary states
  const isFinished = gameState.status === 'finished';
  const isRoundSummary = gameState.status === 'round-summary';
  const winner = isFinished ? gameState.players.find(p => p.id === gameState.winnerId) : null;

  return (
    <div className="tv-mode">
      {/* Top bar */}
      <div className="tv-topbar">
        <div className="tv-topbar__left">
          <span className="tv-topbar__logo">FLIP 7</span>
          <span className="tv-topbar__divider" />
          <span className="tv-topbar__info">Round {gameState.currentRound}</span>
          <span className="tv-topbar__divider" />
          <span className="tv-topbar__info">Target {gameState.targetScore}</span>
          {gameState.variant && gameState.variant !== 'standard' && (
            <>
              <span className="tv-topbar__divider" />
              <span className="tv-topbar__variant">
                {gameState.variant === 'vengeance-brutal' ? 'Brutal' : 'Vengeance'}
              </span>
            </>
          )}
        </div>
        <div className="tv-topbar__right">
          <span className="tv-topbar__info">{gameState.deckRemaining} cards left</span>
          <button onClick={onExitTV} className="tv-topbar__exit">
            Exit TV
          </button>
        </div>
      </div>

      {/* Event toast */}
      {eventToast && (
        <div className="tv-toast badge-pop">{eventToast}</div>
      )}

      {/* Main content */}
      <div className="tv-content">
        {isFinished && winner ? (
          /* Game over */
          <div className="tv-center-message">
            <h1 className="tv-winner-text">{winner.name} Wins!</h1>
            <p className="tv-winner-score">Final Score: {winner.totalScore}</p>
          </div>
        ) : isRoundSummary ? (
          /* Round summary */
          <div className="tv-center-message">
            <h2 className="tv-round-complete">Round {gameState.currentRound} Complete</h2>
          </div>
        ) : (
          /* Current turn indicator */
          currentPlayer && gameState.status === 'playing' && (
            <div className="tv-turn-indicator">
              <span className="tv-turn-label">
                {currentPlayer.avatar && <span style={{ marginRight: '6px' }}>{currentPlayer.avatar}</span>}
                {currentPlayer.name}'s Turn
              </span>
            </div>
          )
        )}

        {/* Player grid */}
        <div className="tv-players-grid">
          {(isFinished || isRoundSummary ? sortedPlayers : gameState.players).map((player) => (
            <PlayerCard
              key={player.id}
              player={player}
              isCurrent={!isFinished && !isRoundSummary && gameState.players.indexOf(player) === gameState.currentPlayerIndex}
              variant={gameState.variant}
            />
          ))}
        </div>

        {/* Round events timeline (round summary only) */}
        {isRoundSummary && gameState.roundEvents.length > 0 && (
          <div className="tv-events">
            {gameState.roundEvents.map((event, i) => (
              <span key={i} className="tv-event" style={{ color: eventColor(event.type) }}>
                {eventIcon(event.type)} {formatRoundEvent(event)}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
